import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Header from "../../../Header/Header";
import VICSMenu from "../../VICSMenu";
import DocMenu from "../../../DocMenu";
import GenPdf from "../../../GenPdf";
import YesNoDialog from "../../../YesNoDialog";
import InformationDialog from "../../../InformationDialog";
import { changeTitleName, changePdfFileName, changeIndication } from "../../../../redux/actions/emailAction";
import { changeVicValue, fetchCompaniesByDossierLot } from "../../../../redux/actions/vicActions";
import { newEntrepriseValue } from "../../../../redux/actions/entrepriseActions";
import { getVicValues, deleteVic, genVicPDF, getVicPDF, getLotName, isVicSigned } from "../../Utils"
import { Button } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { PictureAsPdf as PdfIcon, Visibility as VisibilityIcon, Delete as DeleteIcon } from "@material-ui/icons";
import "../../../../Styles/VIC/VicValidations.scss";

const VicValidations = ({ dispatch, vicValue, dossierValue, lotsValue, companies, entrepriseValue }) => {
  const [disableButton, setDisableButton] = useState(false);
  const [nameCompany, setNameCompany] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogInfo, setOpenDialogInfo] = useState(false);
  const [file, setFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [indicationValue, setIndicationValue] = useState("");
  const prevDossierValue = useRef(dossierValue);
  const prevLotsValue = useRef(lotsValue);
  const prevCompanies = useRef(companies);
  const prevEntrepriseValue = useRef(entrepriseValue);

  // Premier useEffect - ajout de 'dispatch' dans les dépendances
  useEffect(() => {
    const fetchCompaniesData = async () => {
      dispatch(changePdfFileName(""))
      if (prevDossierValue.current !== dossierValue || prevLotsValue.current !== lotsValue) {
        await dispatch(fetchCompaniesByDossierLot(dossierValue, lotsValue));

        prevDossierValue.current = dossierValue;
        prevLotsValue.current = lotsValue;
      }
    }

    fetchCompaniesData();
  }, [dossierValue, lotsValue, dispatch])

  // Deuxième useEffect - ajout de toutes les dépendances manquantes
  useEffect(() => {
    const fetchData = async () => {
      dispatch(changePdfFileName(""))
      if (prevCompanies.current !== companies) {
        if (companies.length !== 0) {
          await dispatch(newEntrepriseValue(companies[0]))
          const result = await getVicValues(dossierValue, lotsValue, companies[0]);
          await dispatch(changeVicValue(result.id))
        } else {
          await dispatch(changeVicValue(""))
        };

        prevCompanies.current = companies;
      }

      if (prevEntrepriseValue.current !== entrepriseValue) {
        const result = await getVicValues(dossierValue, lotsValue, entrepriseValue);

        await dispatch(changeVicValue(result.id))

        prevEntrepriseValue.current = entrepriseValue
      }
    }

    fetchData();
  }, [companies, entrepriseValue, dispatch, dossierValue, lotsValue]);

  useEffect(() => {
    setDisableButton(true);
    const getIndicationValue = async () => {
      const lotName = await getLotName(lotsValue);
      let conmapyName = '';

      try {
        const parsedValue = JSON.parse(entrepriseValue);
        conmapyName = parsedValue.nom || '';
      } catch (e) {
        conmapyName = entrepriseValue.nom || '';
      }

      setIndicationValue(`${lotName} - ${conmapyName}`);
    };

    getIndicationValue();
    setDisableButton(false);
  }, [entrepriseValue, lotsValue]);

  const generateVic = async () => {
    setIsGenerating(true);  // Afficher le spinner
    setFile(null);
    let blob = await genVicPDF(vicValue);
    setIsGenerating(false); // Masquer le spinner
    if (blob) {
      setFile(blob);
      setIsOpen(true);      // Ouvrir la modal avec le PDF généré
    }
  }

  const handleGenClick = async () => {
    setIsGenerating(true);
    const signed = await isVicSigned(vicValue);

    if (!signed) {
      setIsGenerating(false);
      setOpenSignatureDialog(true);
      return;
    }

    await generateVic();
  }

  const handleConfirmGenWithoutSignature = async () => {
    setOpenSignatureDialog(false);
    await generateVic();
  }

  const handleShowPdf = async () => {
    setFile(null);
    let response = await getVicPDF(vicValue, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      setIsOpen(true);

      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName))
    }
  }

  const handleGetPdfFile = async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getVicPDF(vicValue, 'pdf');
    if (response.blob.size > 100) {
      setFile(response.blob);
      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName))
    }
    setDisableButton(false);
  }

  const handleGetWordFile = async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getVicPDF(vicValue, 'word');
    if (response.blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setFile(response.blob);
    }
    setDisableButton(false);
  }

  const deleteCurrentVic = async () => {
    setOpenDialog(false);
    try {
      const result = await deleteVic(vicValue);
      if (result.success) {
        await dispatch(fetchCompaniesByDossierLot(dossierValue, lotsValue));
        setOpenDialogInfo(true);
      }
    } catch (err) {
      console.error("Erreur lors de la suppression du Vic :", err.message);
    }
  };

  const handleChangeTitleName = () => {
    dispatch(changeTitleName(`VIC`));
  };

  return (
    <div className="vicValid">
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
        updateIndication={() => dispatch(changeIndication(indicationValue))}
      />
      <h1 className="title">Validation de la VIC</h1>
      <VICSMenu />
      <div className="data-vicvalidations">
        <div className="buttons-container">
          <Button
            color="primary"
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={() => handleGenClick()}
            disabled={!vicValue || isGenerating}
          >
            Générer
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={() => handleShowPdf()}
            disabled={!vicValue || isGenerating}
          >
            Afficher
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setNameCompany(typeof entrepriseValue === "string" ? JSON.parse(entrepriseValue).nom : entrepriseValue.nom);
              setOpenDialog(true);
            }}
            style={{ backgroundColor: !vicValue || isGenerating ? "" : "red" }}
            disabled={!vicValue || isGenerating}
          >
            Supprimer
          </Button>
          {disableButton || isGenerating && <CircularProgress className="spinner" color="inherit" />}
        </div>
        <YesNoDialog
          isOpen={openDialog}
          title={'SUPPRESSION VIC'}
          question={`Êtes vous sûr de vouloir supprimer la VIC de l'entrprise "${nameCompany}" ?`}
          handleYesClick={() => deleteCurrentVic()}
          handleClose={() => setOpenDialog(false)}
        />
        <InformationDialog
          isOpen={openDialogInfo}
          title={'INFORMATION'}
          information={`La VIC a été supprimée avec succès`}
          handleClose={() => setOpenDialogInfo(false)}
        />
      </div>
      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
      <YesNoDialog
        isOpen={openSignatureDialog}
        title={"VIC NON SIGNÉE"}
        question={`Cette VIC n'est pas encore signée par l'entreprise. Voulez-vous quand même la générer ?`}
        handleYesClick={handleConfirmGenWithoutSignature}
        handleClose={() => setOpenSignatureDialog(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  dossierValue: state.devis.dossierValue,
  vicValue: state.vic.vicValue,
  lotsValue: state.vic.lotsValue,
  companies: state.vic.companies,
  entrepriseValue: state.entreprise.entrepriseValue,
});

export default connect(mapStateToProps)(VicValidations);